import {
  CHORD_QUALITIES,
  MAX_CONTROL_VALUE,
  MAX_MIDI_NOTE,
  MAX_VELOCITY,
  MIN_CONTROL_VALUE,
  MIN_MIDI_NOTE,
  MIN_VELOCITY,
  SEQUENCE_SCHEMA_VERSION,
  TRACK_TYPES,
  type ChordEvent,
  type ChordQuality,
  type ControlEvent,
  type DrumEvent,
  type NoteEvent,
  type SequenceDocument,
  type SequenceEvent,
  type Track,
  type TrackType,
} from './types';

export interface ValidationError {
  readonly path: string;
  readonly code: string;
  readonly message: string;
}

export type ValidationResult =
  | { readonly ok: true; readonly value: SequenceDocument }
  | { readonly ok: false; readonly errors: readonly ValidationError[] };

const IDENTIFIER_PATTERN = /^[A-Za-z_][A-Za-z0-9_.-]*$/;
const MAX_IDENTIFIER_LENGTH = 64;
const MIN_BPM = 30;
const MAX_BPM = 300;
const MIN_LENGTH_STEPS = 1;
const MAX_LENGTH_STEPS = 64;

class Errors {
  private readonly list: ValidationError[] = [];

  add(path: string, code: string, message: string): void {
    this.list.push({ path, code, message });
  }

  get all(): readonly ValidationError[] {
    return this.list;
  }

  get hasAny(): boolean {
    return this.list.length > 0;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value);
}

function checkIdentifier(errors: Errors, path: string, value: unknown, label: string): value is string {
  if (typeof value !== 'string' || value.length === 0) {
    errors.add(path, 'field.type', `${label} muss ein nicht-leerer String sein.`);
    return false;
  }
  if (value.length > MAX_IDENTIFIER_LENGTH) {
    errors.add(path, 'field.tooLong', `${label} darf hoechstens ${MAX_IDENTIFIER_LENGTH} Zeichen lang sein.`);
    return false;
  }
  if (!IDENTIFIER_PATTERN.test(value)) {
    errors.add(path, 'field.pattern', `${label} muss auf ${IDENTIFIER_PATTERN.source} passen.`);
    return false;
  }
  return true;
}

function checkNonEmptyString(errors: Errors, path: string, value: unknown, label: string): value is string {
  if (typeof value !== 'string' || value.length === 0) {
    errors.add(path, 'field.type', `${label} muss ein nicht-leerer String sein.`);
    return false;
  }
  return true;
}

function checkNumberRange(
  errors: Errors,
  path: string,
  value: unknown,
  label: string,
  min: number,
  max: number,
  integer: boolean,
): value is number {
  if (integer ? !isInteger(value) : !isFiniteNumber(value)) {
    errors.add(path, 'field.type', `${label} muss eine ${integer ? 'ganze Zahl' : 'Zahl'} sein.`);
    return false;
  }
  const numeric = value as number;
  if (numeric < min || numeric > max) {
    errors.add(path, 'field.range', `${label} muss zwischen ${min} und ${max} liegen (war ${numeric}).`);
    return false;
  }
  return true;
}

function checkBoolean(errors: Errors, path: string, value: unknown, label: string): value is boolean {
  if (typeof value !== 'boolean') {
    errors.add(path, 'field.type', `${label} muss ein Boolean sein.`);
    return false;
  }
  return true;
}

/**
 * Validiert eine unbekannte Eingabe gegen oib.sequence/0.1.
 *
 * Eine falsche/fehlende schemaVersion fuehrt bewusst zu genau einem Fehler
 * statt zu einer Lawine von Folgefehlern (gleiche Konvention wie
 * oib.instrument/0.1 in Open Instrument Blueprint).
 */
export function validateSequence(input: unknown): ValidationResult {
  const errors = new Errors();

  if (!isRecord(input)) {
    errors.add('', 'doc.notAnObject', 'Das Dokument muss ein JSON-Objekt sein.');
    return { ok: false, errors: errors.all };
  }

  const schemaVersion = input['schemaVersion'];
  if (schemaVersion === undefined) {
    errors.add('/schemaVersion', 'schemaVersion.missing', 'schemaVersion fehlt.');
    return { ok: false, errors: errors.all };
  }
  if (schemaVersion !== SEQUENCE_SCHEMA_VERSION) {
    errors.add(
      '/schemaVersion',
      'schemaVersion.unsupported',
      `Nicht unterstuetzte schemaVersion "${String(schemaVersion)}". Erwartet: "${SEQUENCE_SCHEMA_VERSION}".`,
    );
    return { ok: false, errors: errors.all };
  }

  checkIdentifier(errors, '/id', input['id'], 'id');
  checkNonEmptyString(errors, '/name', input['name'], 'name');
  checkNonEmptyString(errors, '/version', input['version'], 'version');

  if (input['description'] !== undefined && typeof input['description'] !== 'string') {
    errors.add('/description', 'field.type', 'description muss ein String sein.');
  }
  if (input['author'] !== undefined && typeof input['author'] !== 'string') {
    errors.add('/author', 'field.type', 'author muss ein String sein.');
  }
  if (input['tags'] !== undefined) {
    validateStringArray(errors, '/tags', input['tags'], 'tags');
  }

  const timing = input['timing'];
  validateTiming(errors, timing);

  const lengthSteps = isRecord(timing) && isInteger(timing['lengthSteps']) ? (timing['lengthSteps'] as number) : undefined;

  const trackIds = new Set<string>();
  const tracksRaw = input['tracks'];
  if (!Array.isArray(tracksRaw)) {
    errors.add('/tracks', 'field.type', 'tracks muss ein Array sein.');
  } else {
    tracksRaw.forEach((track, index) => validateTrack(errors, `/tracks/${index}`, track, trackIds));
  }

  const eventIds = new Set<string>();
  const eventsRaw = input['events'];
  const trackById = new Map<string, TrackType>();
  if (Array.isArray(tracksRaw)) {
    for (const track of tracksRaw) {
      if (isRecord(track) && typeof track['id'] === 'string' && typeof track['type'] === 'string') {
        trackById.set(track['id'], track['type'] as TrackType);
      }
    }
  }
  if (!Array.isArray(eventsRaw)) {
    errors.add('/events', 'field.type', 'events muss ein Array sein.');
  } else {
    eventsRaw.forEach((event, index) =>
      validateEvent(errors, `/events/${index}`, event, eventIds, trackById, lengthSteps),
    );
  }

  if (errors.hasAny) {
    return { ok: false, errors: errors.all };
  }

  return { ok: true, value: input as unknown as SequenceDocument };
}

function validateStringArray(errors: Errors, path: string, value: unknown, label: string): void {
  if (!Array.isArray(value)) {
    errors.add(path, 'field.type', `${label} muss ein Array aus Strings sein.`);
    return;
  }
  value.forEach((item, index) => {
    if (typeof item !== 'string') {
      errors.add(`${path}/${index}`, 'field.type', `${label}[${index}] muss ein String sein.`);
    }
  });
}

function validateTiming(errors: Errors, timing: unknown): void {
  if (!isRecord(timing)) {
    errors.add('/timing', 'field.type', 'timing muss ein Objekt sein.');
    return;
  }
  checkNumberRange(errors, '/timing/bpm', timing['bpm'], 'bpm', MIN_BPM, MAX_BPM, false);

  const timeSignature = timing['timeSignature'];
  if (!isRecord(timeSignature)) {
    errors.add('/timing/timeSignature', 'field.type', 'timeSignature muss ein Objekt sein.');
  } else {
    checkNumberRange(errors, '/timing/timeSignature/numerator', timeSignature['numerator'], 'numerator', 1, 64, true);
    checkNumberRange(
      errors,
      '/timing/timeSignature/denominator',
      timeSignature['denominator'],
      'denominator',
      1,
      64,
      true,
    );
  }

  checkNumberRange(errors, '/timing/stepsPerBeat', timing['stepsPerBeat'], 'stepsPerBeat', 1, 16, true);
  checkNumberRange(
    errors,
    '/timing/lengthSteps',
    timing['lengthSteps'],
    'lengthSteps',
    MIN_LENGTH_STEPS,
    MAX_LENGTH_STEPS,
    true,
  );
  checkBoolean(errors, '/timing/loop', timing['loop'], 'loop');

  if (timing['swing'] !== undefined) {
    checkNumberRange(errors, '/timing/swing', timing['swing'], 'swing', 0, 1, false);
  }
}

function validateTrack(errors: Errors, path: string, track: unknown, trackIds: Set<string>): void {
  if (!isRecord(track)) {
    errors.add(path, 'field.type', 'Track muss ein Objekt sein.');
    return;
  }
  const id = track['id'];
  if (checkIdentifier(errors, `${path}/id`, id, 'id')) {
    if (trackIds.has(id)) {
      errors.add(`${path}/id`, 'id.duplicate', `Doppelte Track-ID "${id}".`);
    } else {
      trackIds.add(id);
    }
  }
  checkNonEmptyString(errors, `${path}/name`, track['name'], 'name');
  checkNonEmptyString(errors, `${path}/role`, track['role'], 'role');

  const type = track['type'];
  if (typeof type !== 'string' || !TRACK_TYPES.includes(type as TrackType)) {
    errors.add(`${path}/type`, 'field.enum', `type muss eines von ${TRACK_TYPES.join(', ')} sein.`);
  }
  checkBoolean(errors, `${path}/mute`, track['mute'], 'mute');
  checkBoolean(errors, `${path}/solo`, track['solo'], 'solo');
  checkNumberRange(errors, `${path}/level`, track['level'], 'level', 0, 1, false);
}

function validateEvent(
  errors: Errors,
  path: string,
  event: unknown,
  eventIds: Set<string>,
  trackById: ReadonlyMap<string, TrackType>,
  lengthSteps: number | undefined,
): void {
  if (!isRecord(event)) {
    errors.add(path, 'field.type', 'Event muss ein Objekt sein.');
    return;
  }

  const id = event['id'];
  if (checkIdentifier(errors, `${path}/id`, id, 'id')) {
    if (eventIds.has(id)) {
      errors.add(`${path}/id`, 'id.duplicate', `Doppelte Event-ID "${id}".`);
    } else {
      eventIds.add(id);
    }
  }

  const step = event['step'];
  if (!isInteger(step)) {
    errors.add(`${path}/step`, 'field.type', 'step muss eine ganze Zahl sein.');
  } else if (step < 0 || (lengthSteps !== undefined && step >= lengthSteps)) {
    errors.add(
      `${path}/step`,
      'event.stepOutOfRange',
      `step ${step} liegt ausserhalb des Patterns (0..${lengthSteps !== undefined ? lengthSteps - 1 : '?'}).`,
    );
  }

  const trackId = event['trackId'];
  let trackType: TrackType | undefined;
  if (typeof trackId !== 'string' || trackId.length === 0) {
    errors.add(`${path}/trackId`, 'field.type', 'trackId muss ein nicht-leerer String sein.');
  } else if (!trackById.has(trackId)) {
    errors.add(`${path}/trackId`, 'ref.unknownTrack', `Unbekannte Track-ID "${trackId}".`);
  } else {
    trackType = trackById.get(trackId);
  }

  const kind = event['kind'];
  if (typeof kind !== 'string' || !TRACK_TYPES.includes(kind as TrackType)) {
    errors.add(`${path}/kind`, 'field.enum', `kind muss eines von ${TRACK_TYPES.join(', ')} sein.`);
    return;
  }
  if (trackType !== undefined && kind !== trackType) {
    errors.add(
      `${path}/kind`,
      'event.trackTypeMismatch',
      `Event-Art "${kind}" passt nicht zum Tracktyp "${trackType}" von "${trackId as string}".`,
    );
  }

  switch (kind as TrackType) {
    case 'note':
      validateNoteEvent(errors, path, event);
      break;
    case 'chord':
      validateChordEvent(errors, path, event);
      break;
    case 'drum':
      validateDrumEvent(errors, path, event);
      break;
    case 'control':
      validateControlEvent(errors, path, event);
      break;
  }
}

function validateNoteEvent(errors: Errors, path: string, event: Record<string, unknown>): void {
  const hasNote = event['note'] !== undefined;
  const hasDegree = event['scaleDegree'] !== undefined;
  if (hasNote && hasDegree) {
    errors.add(path, 'note.targetAmbiguous', 'Nur eines von note/scaleDegree darf gesetzt sein.');
  } else if (!hasNote && !hasDegree) {
    errors.add(path, 'note.targetMissing', 'note oder scaleDegree muss gesetzt sein.');
  } else if (hasNote) {
    checkNumberRange(errors, `${path}/note`, event['note'], 'note', MIN_MIDI_NOTE, MAX_MIDI_NOTE, true);
  } else {
    if (!isInteger(event['scaleDegree'])) {
      errors.add(`${path}/scaleDegree`, 'field.type', 'scaleDegree muss eine ganze Zahl sein.');
    }
  }
  checkNumberRange(errors, `${path}/velocity`, event['velocity'], 'velocity', MIN_VELOCITY, MAX_VELOCITY, true);
  validateGate(errors, `${path}/gate`, event['gate']);
}

function validateChordEvent(errors: Errors, path: string, event: Record<string, unknown>): void {
  if (!isInteger(event['degree'])) {
    errors.add(`${path}/degree`, 'field.type', 'degree muss eine ganze Zahl sein.');
  }
  const quality = event['quality'];
  if (typeof quality !== 'string' || !CHORD_QUALITIES.includes(quality as ChordQuality)) {
    errors.add(`${path}/quality`, 'field.enum', `quality muss eines von ${CHORD_QUALITIES.join(', ')} sein.`);
  }
  checkNumberRange(errors, `${path}/velocity`, event['velocity'], 'velocity', MIN_VELOCITY, MAX_VELOCITY, true);
  validateGate(errors, `${path}/gate`, event['gate']);
}

function validateDrumEvent(errors: Errors, path: string, event: Record<string, unknown>): void {
  checkIdentifier(errors, `${path}/triggerId`, event['triggerId'], 'triggerId');
  checkNumberRange(errors, `${path}/velocity`, event['velocity'], 'velocity', MIN_VELOCITY, MAX_VELOCITY, true);
}

function validateControlEvent(errors: Errors, path: string, event: Record<string, unknown>): void {
  checkIdentifier(errors, `${path}/controlId`, event['controlId'], 'controlId');
  checkNumberRange(errors, `${path}/value`, event['value'], 'value', MIN_CONTROL_VALUE, MAX_CONTROL_VALUE, false);
  if (event['stateRef'] !== undefined) {
    checkNonEmptyString(errors, `${path}/stateRef`, event['stateRef'], 'stateRef');
  }
  if (event['sceneRef'] !== undefined) {
    checkNonEmptyString(errors, `${path}/sceneRef`, event['sceneRef'], 'sceneRef');
  }
}

function validateGate(errors: Errors, path: string, value: unknown): void {
  if (!isFiniteNumber(value) || value <= 0) {
    errors.add(path, 'event.gateInvalid', 'gate muss eine Zahl groesser 0 sein.');
  }
}

export function assertNoteEvent(event: SequenceEvent): event is NoteEvent {
  return event.kind === 'note';
}
export function assertChordEvent(event: SequenceEvent): event is ChordEvent {
  return event.kind === 'chord';
}
export function assertDrumEvent(event: SequenceEvent): event is DrumEvent {
  return event.kind === 'drum';
}
export function assertControlEvent(event: SequenceEvent): event is ControlEvent {
  return event.kind === 'control';
}

export function trackTypeOf(track: Track): TrackType {
  return track.type;
}
